import React, { useRef } from 'react'

import styled, { useTheme, CSS, css } from 'styled-components'

import { Heading, Paragraph, ResponsiveImage, Button, Spacer, Link } from '@atoms/index'
import { DeviceContainer } from '@client/components/utility'
import { LargePageMediaImage, LargePageMediaImageFloat, MediumBoxShadow, PageMediaImage, PageMediaImageFloat, ResponsivePXValue } from '@components/Theme'
import { PageMediaSectionFragment } from '@hooks/api'
import { ImageSizeEnum } from '@uctypes/api/globalTypes'

import { PageSectionRef } from './PageSectionGroup'

const MobileRightContainer = styled.div`
  width: 100%;
`

const Small = css`
  ${PageMediaImage}
  ${PageMediaImageFloat}
`

const Medium = css`
  ${PageMediaImage}
  ${PageMediaImageFloat}
`

const Large = css`
  ${LargePageMediaImage}
  ${LargePageMediaImageFloat}
`

const MobileRightImageContainer = styled.div<{ $shadow: boolean, $size: ImageSizeEnum }>`

  ${(props): CSS => props.$shadow ? MediumBoxShadow : undefined}
  background-color: ${(props): string => props.$shadow ? props.theme.colors.whites.desertStorm : props.theme.colors.misc.transparent};

  ${(props): CSS => {
    switch (props.$size) {
      case ImageSizeEnum.SMALL:
        return Small
      case ImageSizeEnum.MEDIUM:
        return Medium
      case ImageSizeEnum.LARGE:
        return Large
    }
  }}
  
  ${ResponsivePXValue('margin-bottom', { mobile: '-48px', tablet: '-100px' })}

`

const MobileContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
`

const MobileButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;

  ${ResponsivePXValue('margin', { mobile: '32px 0 16px', tablet: '80px 0 40px' })}
`

const DesktopRightContainer = styled.div`
  display: flex;
  width: 100%;
`

const DesktopSmall = css`
  ${PageMediaImage}
  ${PageMediaImageFloat}
`

const DesktopMedium = css`
  ${PageMediaImage}
  ${PageMediaImageFloat}
  ${ResponsivePXValue('margin-right', { desktop: '72px' })}
`

const DesktopLarge = css`
  ${LargePageMediaImage}
  ${LargePageMediaImageFloat}
`

const DesktopRightImageContainer = styled.div<{ $shadow: boolean, $size: ImageSizeEnum }>`
  flex-shrink: 0;
  ${(props): CSS => props.$shadow ? MediumBoxShadow : undefined}
  background-color: ${(props): string => props.$shadow ? props.theme.colors.whites.desertStorm : props.theme.colors.misc.transparent};
 
  ${(props): CSS => {
    switch (props.$size) {
      case ImageSizeEnum.SMALL:
        return DesktopSmall
      case ImageSizeEnum.MEDIUM:
        return DesktopMedium
      case ImageSizeEnum.LARGE:
        return DesktopLarge
      default:
        break
    }
  }}
`

const DesktopContentContainer = styled.div`
  ${ResponsivePXValue('padding', { desktop: '42px 84px 0' })}
`

const DesktopButtonsContainer = styled.div`
  display: flex;
  ${ResponsivePXValue('margin', { desktop: '30px 0;' })}
`

const TextContainer = styled.div`
`

export interface PageMediaRightStandardProps {
  pageSection: PageMediaSectionFragment
  pageSectionRef?: boolean
  addPageSectionRef?: (pageSectionRef: PageSectionRef) => void
}

export function PageMediaRightStandard({ pageSection, pageSectionRef = false, addPageSectionRef }: PageMediaRightStandardProps): JSX.Element {

  const theme = useTheme()
  let pageRef
  let idx: number
  let content: string

  const hasButton = !!pageSection.links?.primary || !!pageSection.links?.secondary

  if (pageSectionRef && addPageSectionRef) {
    pageRef = useRef()
    addPageSectionRef({ ref: pageRef, id: pageSection.id })
  }

  return (
    <>
      <DeviceContainer $mobile $tablet>
        <MobileRightContainer>
          <MobileRightImageContainer $shadow={pageSection.imageShadow} $size={pageSection.imageSize}>
            <ResponsiveImage image={pageSection.image} objectFit={pageSection?.mobileObjectFit} />
          </MobileRightImageContainer>
          <MobileContentContainer>
            <TextContainer>
              <Heading variant='h4' align='center' fontWeight={600} color={theme.colors.slates.bitter}>
                {pageSection.title}
              </Heading>
              <Spacer universal='16px' />
              <Heading variant='h2' align='center'>
                {pageSection.subTitle}
              </Heading>
              <Spacer universal='16px' />
              <For each='content' index='idx' of={pageSection.contents}>
                <Paragraph align='center' key={idx}>
                  {content}
                </Paragraph>
              </For>
            </TextContainer>
            <If condition={hasButton}>
              <MobileButtonsContainer>
                <If condition={!!pageSection.links?.primary}>
                  <Button title={pageSection.links.primary.title} href={pageSection.links.primary.link} />
                </If>
                <If condition={!!pageSection.links?.secondary}>
                  <Link href={pageSection.links.secondary.link}>{pageSection.links.secondary.title}</Link>
                </If>
              </MobileButtonsContainer>
            </If>
          </MobileContentContainer>
        </MobileRightContainer>
      </DeviceContainer>
      <DeviceContainer $desktop>
        <DesktopRightContainer>
          <DesktopContentContainer>
            <TextContainer>
              <Heading variant='h4' fontWeight={600} color={theme.colors.slates.bitter}>
                {pageSection.title}
              </Heading>
              <Spacer universal='16px' />
              <Heading variant='h2'>
                {pageSection.subTitle}
              </Heading>
              <Spacer universal='16px' />
              <For each='content' index='idx' of={pageSection.contents}>
                <Paragraph align='left' key={idx}>
                  {content}
                </Paragraph>
              </For>
            </TextContainer>
            <If condition={hasButton}>
              <DesktopButtonsContainer>
                <If condition={!!pageSection.links?.primary}>
                  <Button title={pageSection.links.primary.title} href={pageSection.links.primary.link} />
                </If>
                <If condition={!!pageSection.links?.secondary}>
                  <Link href={pageSection.links.secondary.link}>{pageSection.links.secondary.title}</Link>
                </If>
              </DesktopButtonsContainer>
            </If>
          </DesktopContentContainer>
          <DesktopRightImageContainer $shadow={pageSection.imageShadow} $size={pageSection.imageSize}>
            <ResponsiveImage image={pageSection.image} objectFit={pageSection?.objectFit} />
          </DesktopRightImageContainer>
        </DesktopRightContainer>
      </DeviceContainer>
    </>
  )

}
